import React, { FC } from "react";
import {
  Stack,
  Flex,
  Box,
  Text,
  AspectRatio,
  HStack,
  useMediaQuery,
} from "@chakra-ui/react";
import {
  GatsbyImage,
  getImage,
  IGatsbyImageData,
  StaticImage,
} from "gatsby-plugin-image";

type BlogHeroType = {
  imageData?: IGatsbyImageData;
  title?: string;
  excerpt?: string;
  date?: string;
  author: string;
  category: string;
};

const BlogHeroPostSection: FC<BlogHeroType> = ({
  imageData,
  title,
  date,
  author,
  category,
  excerpt,
}) => {
  const image = imageData ? getImage(imageData) : null;

  const [isBase] = useMediaQuery("(max-width: 479px)");
  const [isSm] = useMediaQuery("(min-width: 480px) and (max-width: 758px)");
  const [isMd] = useMediaQuery("(min-width: 759px) and (max-width: 959px)");
  const [isLg] = useMediaQuery("(min-width: 960px)");

  let size;
  if (isBase) {
    size = "753px";
  } else if (isSm) {
    size = "802px";
  } else if (isMd) {
    size = "1000px";
  } else if (isLg) {
    size = "573px";
  }

  return (
    <>
      <Box position="relative" h={size} w="100%">
        <StaticImage
          src="../../../images/hero_bg.webp"
          alt="hero bg image"
          layout="fullWidth"
          style={{
            height: "100%",
            width: "100%",
            objectFit: "cover",
            objectPosition: "center",
            backgroundColor: "transparent !important",
            position: "absolute",
          }}
          placeholder="none"
          loading="lazy"
          quality={100}
        />
        <Stack
          px={{ base: 4, "2xl": 0 }}
          h="100%"
          maxW="1280px"
          mx="auto"
          w="100%"
          position="relative"
          zIndex={1}
          spacing={10}
          py={{ base: 10, md: 20 }}
          direction={{ base: "column", lg: "row" }}
        >
          <Flex alignItems="flex-start" justify="center" w="full">
            <Box h="100%" w="100%">
              <AspectRatio ratio={16 / 9}>
                {image ? (
                  <GatsbyImage
                    image={image}
                    alt="Blog Featured Image"
                    style={{
                      borderRadius: "8px",
                      minHeight: "330px",
                      width: "100%",
                      maxHeight: "400px",
                      maxWidth: "560px",
                      minWidth: "288px",
                    }}
                    imgStyle={{
                      objectFit: "cover",
                      borderRadius: "8px",
                    }}
                  />
                ) : (
                  <StaticImage
                    src="../../../images/blogPlaceholder.png"
                    alt="Blog Featured Image"
                    layout="fullWidth"
                    quality={100}
                    style={{
                      borderRadius: "8px",
                      minHeight: "330px",
                      width: "100%",
                    }}
                    imgStyle={{
                      objectFit: "cover",
                      borderRadius: "8px",
                    }}
                  />
                )}
              </AspectRatio>
            </Box>
          </Flex>

          <Stack
            spacing={0}
            w="100%"
            justifyContent={{ base: "flex-end", lg: "flex-start" }}
            h="100%"
          >
            <Text
              maxW={{ base: "560px", lg: "650px" }}
              fontSize={{ base: "36px", md: "72px" }}
              lineHeight="100%"
              color="#1B1E1E"
              className="light-text"
              mt={{ base: 0, md: "8px" }}
              pb={5}
              as="h1"
            >
              {title}
            </Text>
            <Text
              color="#6A7471"
              fontSize="16px"
              className="regular-text"
              dangerouslySetInnerHTML={{ __html: excerpt || "" }}
              pb={4}
            />

            <HStack
              maxW={{ base: "560px", lg: "650px" }}
              wrap="wrap"
              textAlign="start"
              w="100%"
              color="white"
              gap={3}
              mt={{ base: 1, md: 0 }}
            >
              <Text color="#116266" fontSize="14px" className="regular-text">
                {category.toUpperCase()}
              </Text>
              <Text className="regular-text" fontSize="14px" color="#949D9B">
                {date}
              </Text>
              <Text fontSize="14px" className="regular-text" color="#949D9B">
                By {author}
              </Text>
            </HStack>
          </Stack>
        </Stack>
      </Box>
    </>
  );
};

export default BlogHeroPostSection;
